import { action, makeObservable, runInAction } from 'mobx';
import { downloadCensusTemplate } from 'global/utils/downloadFiles';
import axios from 'axios';
import { uploadFiles } from 'global/utils/uploadFiles';
import { doesGroupHaveCensus, getGroupCensusDownloadUrls } from 'api/operations/censusService';
import { celticsRights } from '@insurancemenu/imenu-components-lib/dist/utils/accessRights';
import { CensusModuleDependencies, ICensusModule } from '../iCensusModule';

export interface CensusGroupDomainDependencies extends CensusModuleDependencies {
  groupId: string;
}

export class CensusGroupModule extends ICensusModule<CensusGroupDomainDependencies> {
  public async uploadExistingCensus(): Promise<void> {
    const info = await doesGroupHaveCensus(this._domainDependencies.groupId);
    runInAction(() => {
      this._existingCensusInfo = info;
    });
  }

  public async uploadCensus(): Promise<void> {
    if (!this._uploadedFiles) {
      throw new Error('No files uploaded!');
    }
    this.setIsLoading(true);
    const uploadedFileNames = Object.keys(this._uploadedFiles).join(':');
    // send file names and get list of urls
    const res = await axios<Record<string, string>>({
      url: `https://${process.env.REACT_APP_API_BASE_URL}/censusV2/Prod/proposalUpload?groupId=${this._domainDependencies.groupId}&fileNames=${uploadedFileNames}`,
      withCredentials: true,
    });
    try {
      await runInAction(async () => {
        await uploadFiles(this._uploadedFiles!, res.data);
      });
    } catch (err) {
      this.setCensusHasErrors(true);
    } finally {
      this.setIsLoading(false);
    }
  }

  public async getPreviouslyUploadedFiles(): Promise<void> {
    if (celticsRights(this._rootStore.userStore.userRoles)) {
      const urls = await getGroupCensusDownloadUrls(this._domainDependencies.groupId);
      if (urls.length > 0) {
        this._setPreviouslyUploadedFilesUrls(urls);
      }
    }
  }

  // We left it like this for the consistency with the enrollment-based module.
  // eslint-disable-next-line class-methods-use-this
  public async downloadCensusTemplate(): Promise<void> {
    await downloadCensusTemplate(`https://${process.env.REACT_APP_API_BASE_URL}/censusV2Binary/Prod/proposalTemplate`);
  }

  constructor(domainDependencies: CensusGroupDomainDependencies) {
    super(domainDependencies);
    makeObservable<CensusGroupModule>(this, {
      uploadExistingCensus: action.bound,
      uploadCensus: action.bound,
      getPreviouslyUploadedFiles: action.bound,
      downloadCensusTemplate: action.bound,
    });
  }
}
